import {
  SceneMode,
  Color,
  defined,
  HorizontalOrigin,
  VerticalOrigin,
  ScreenSpaceEventType,
  ScreenSpaceEventHandler,
  Viewer,
  Entity,
  Clock,
  Cartesian3,
  Cartographic,
  Math as CesiumMath,
} from "cesium";
import {
  IPointGeometry,
  IPolygonGeometry,
  IPolylineGeometry,
} from "@/types/cesiumMap";

//load javascripts
export async function loadScripts(scriptUrls: string[]): Promise<any> {
  const promises = new Array<Promise<any>>();
  scriptUrls.forEach((url) => {
    promises.push(
      new Promise((resolve, reject) => {
        const scriptElement = document.createElement("script");
        scriptElement.src = url;
        scriptElement.onload = resolve;
        scriptElement.onerror = reject;
        document.body.appendChild(scriptElement);
      })
    );
  });
  return Promise.allSettled(promises);
}

//load css
export async function loadCSS(cssUrls: string[]): Promise<any> {
  const promises = new Array<Promise<any>>();
  cssUrls.forEach((url) => {
    promises.push(
      new Promise((resolve, reject) => {
        const linkElement = document.createElement("link");
        linkElement.href = url;
        linkElement.rel = "stylesheet";
        linkElement.onload = resolve;
        linkElement.onerror = reject;
        document.body.appendChild(linkElement);
      })
    );
  });
  return Promise.allSettled(promises);
}

//海拔高度转为图层级别
export function altitudeToZoom(altitude: any) {
  const A = 40487.57;
  const B = 0.00007096758;
  const C = 91610.74;
  const D = -40467.74;
  return D + (A - D) / (1 + Math.pow(altitude / C, B));
}

//图层级别转为海拔高度
export function zoomToAltitude(zoom: any) {
  const A = 40487.57;
  const B = 0.00007096758;
  const C = 91610.74;
  const D = -40467.74;
  return C * Math.pow((A - zoom) / (zoom - D), 1 / B);
}

export function pickPositionFromScreen(screenPosition: any, viewer: any): any {
  //判断三种情况：有高程数据和无高程数据的3维模式、2维模式
  //@notice 关于深度检测depthTest
  //True if primitives such as billboards, polylines, labels, etc. should be depth-tested against the terrain surface,
  //or false if such primitives should always be drawn on top of terrain unless they're on the opposite side of the globe.
  //The disadvantage of depth testing primitives against terrain is that slight numerical noise or terrain level-of-detail switched can sometimes make a primitive that should be on the surface disappear underneath it.
  let pickedPosition: any;
  if (viewer.scene.mode == SceneMode.SCENE2D) {
    //关闭深度检测
    if (viewer.scene.globe.depthTestAgainstTerrain) {
      viewer.scene.globe.depthTestAgainstTerrain = false;
    }
    //从椭球体上获取坐标点
    pickedPosition = viewer.camera.pickEllipsoid(
      screenPosition,
      viewer.scene.globe.ellipsoid
    );
  } else if (
    viewer.scene.mode == SceneMode.COLUMBUS_VIEW ||
    viewer.scene.mode == SceneMode.SCENE3D
  ) {
    //开启深度检测
    if (!viewer.scene.globe.depthTestAgainstTerrain) {
      viewer.scene.globe.depthTestAgainstTerrain = true;
    }
    //从地形表面获取坐标点
    pickedPosition = viewer.scene.pickPosition(screenPosition);
  } else if (viewer.scene.mode == SceneMode.MORPHING) {
    //@todo 暂时没有这种情况
  } else {
    //报错
    console.log("fatal error! viewer scene mode error!");
  }
  return pickedPosition;
}

// export function pickFromScreen(screenPosition: any, viewer: any): any {
//   //判断三种情况：有高程数据和无高程数据的3维模式、2维模式
//   let picked: any;
//   if (viewer.scene.mode == SceneMode.SCENE2D) {
//     //关闭深度检测
//     if (viewer.scene.globe.depthTestAgainstTerrain) {
//       viewer.scene.globe.depthTestAgainstTerrain = false;
//     }
//     //从椭球体上获取坐标点
//     picked = viewer.camera.pickEllipsoid(
//       screenPosition,
//       viewer.scene.globe.ellipsoid
//     );
//   } else if (
//     viewer.scene.mode == SceneMode.COLUMBUS_VIEW ||
//     viewer.scene.mode == SceneMode.SCENE3D
//   ) {
//     //开启深度检测
//     if (!viewer.scene.globe.depthTestAgainstTerrain) {
//       viewer.scene.globe.depthTestAgainstTerrain = true;
//     }
//     //从地形表面获取坐标点
//     picked = viewer.scene.pickPosition(screenPosition);
//   } else if (viewer.scene.mode == SceneMode.MORPHING) {
//     //@todo 暂时没有这种情况
//   } else {
//     //报错
//     console.log("fatal error! viewer scene mode error!");
//   }
//   return picked;
// }

//将[r,g,b,a]转化为cesium.color
export function getColorFromArray(color: any): any {
  color = color as number[];
  if (!defined(color) || color.length < 3) {
    return Color.AQUA;
  }
  const red = Color.byteToFloat(color[0]);
  const green = Color.byteToFloat(color[1]);
  const blue = Color.byteToFloat(color[2]);
  const alpha = color[3] || 1;
  return new Color(red, green, blue, alpha);
}

//获取水平锚点位置
export function getHAnchorFromString(anchor: string | undefined): any {
  if (anchor == "left") {
    return HorizontalOrigin.LEFT;
  } else if (anchor == "center") {
    return HorizontalOrigin.CENTER;
  } else if (anchor == "right") {
    return HorizontalOrigin.RIGHT;
  } else {
    return HorizontalOrigin.CENTER;
  }
}

//获取垂直锚点位置
export function getVAnchorFromString(anchor: string | undefined): any {
  if (anchor == "top") {
    return VerticalOrigin.TOP;
  } else if (anchor == "center") {
    return VerticalOrigin.CENTER;
  } else if (anchor == "bottom") {
    return VerticalOrigin.BOTTOM;
  } else if (anchor == "baseline") {
    return VerticalOrigin.BASELINE;
  } else {
    return VerticalOrigin.CENTER;
  }
}

//深拷贝json对象(会丢失undefined、function)
export function copyObject(obj: Object): Object | undefined {
  if (obj == undefined) {
    return undefined;
  }
  return JSON.parse(JSON.stringify(obj));
}

//解析entity中的几何属性
export function parseEntityGeometry(
  entity: Entity,
  clock: Clock
): IPointGeometry | IPolylineGeometry | IPolygonGeometry | undefined {
  //当一个entity有多个几何属性时，认为优先级如下：point -> polyline -> polygon
  if (entity.point) {
    const position = entity.position?.getValue(clock.currentTime);
    if (position) {
      //笛卡尔坐标系转为地理坐标系，默认wgs84
      const carto = Cartographic.fromCartesian(position);
      return {
        x: CesiumMath.toDegrees(carto.longitude),
        y: CesiumMath.toDegrees(carto.latitude),
        z: carto.height,
      };
    }
  } else if (entity.polyline) {
    const positions = entity.polyline.positions?.getValue(clock.currentTime);
    if (positions && positions.length > 0) {
      const paths = new Array<IPointGeometry>();
      positions.forEach((position: Cartesian3) => {
        const carto = Cartographic.fromCartesian(position);
        paths.push({
          x: CesiumMath.toDegrees(carto.longitude),
          y: CesiumMath.toDegrees(carto.latitude),
          z: carto.height,
        });
      });
      return {
        paths: paths,
      };
    }
  } else if (entity.polygon) {
    const hierarchy = entity.polygon.hierarchy?.getValue(clock.currentTime);
    const positions = hierarchy.positions;
    if (positions && positions.length > 0) {
      const rings = new Array<IPointGeometry>();
      positions.forEach((position: Cartesian3) => {
        const carto = Cartographic.fromCartesian(position);
        rings.push({
          x: CesiumMath.toDegrees(carto.longitude),
          y: CesiumMath.toDegrees(carto.latitude),
          z: carto.height,
        });
      });
      return {
        rings: rings,
        height: entity.polygon.height?.getValue(clock.currentTime),
      };
    }
  }
  return undefined;
}
